import React, { useState, useEffect, useCallback } from 'react';
import { getBets } from '../api/mongoServices';
import EditBetModal from './EditBetModal';
import { motion } from 'framer-motion';
import { FiFilter, FiSearch, FiEdit2, FiRefreshCw } from 'react-icons/fi';

const sortOptions = [
  { label: 'Amount (Low to High)', value: 'amount_asc' },
  { label: 'Amount (High to Low)', value: 'amount_desc' },
  { label: 'Time (Oldest First)', value: 'time_asc' },
  { label: 'Time (Newest First)', value: 'time_desc' },
];

const LuckyDrawOversight = () => {
  const [bets, setBets] = useState([]);
  const [filters, setFilters] = useState({
    agent: '',
    customer: '',
    startDate: '',
    endDate: ''
  });
  const [selectedBet, setSelectedBet] = useState(null);
  const [sortBy, setSortBy] = useState('time_desc');
  const [loading, setLoading] = useState(false);

  const loadBets = useCallback(async () => {
    setLoading(true);
    const data = await getBets(filters);
    let sorted = [...data];
    switch (sortBy) {
      case 'amount_asc':
        sorted.sort((a, b) => a.amount - b.amount);
        break;
      case 'amount_desc':
        sorted.sort((a, b) => b.amount - a.amount);
        break;
      case 'time_asc':
        sorted.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        break;
      case 'time_desc':
        sorted.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        break;
      default:
        break;
    }
    setBets(sorted);
    setLoading(false);
  }, [filters, sortBy]);

  useEffect(() => {
    loadBets();
  }, [loadBets]);

  const totalAmount = bets.reduce((sum, bet) => sum + (bet.amount || 0), 0);
  const averageAmount = bets.length > 0 ? Math.round(totalAmount / bets.length) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-between items-start mb-12"
        >
          <div>
            <h1 className="text-5xl font-bold bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent mb-2">
              🎯 Lucky Draw Oversight
            </h1>
            <p className="text-gray-400 text-lg">Monitor all lucky draw bets and entries</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={loadBets}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-semibold shadow-lg transition"
          >
            <FiRefreshCw size={20} /> Refresh
          </motion.button>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 shadow-xl hover:border-blue-500 transition">
            <p className="text-gray-400 text-sm font-medium">Total Bets</p>
            <p className="text-4xl font-bold text-blue-400 mt-2">{bets.length}</p>
          </div>
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 shadow-xl hover:border-green-500 transition">
            <p className="text-gray-400 text-sm font-medium">Total Amount</p>
            <p className="text-4xl font-bold text-green-400 mt-2">{totalAmount.toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 shadow-xl hover:border-purple-500 transition">
            <p className="text-gray-400 text-sm font-medium">Average Bet</p>
            <p className="text-4xl font-bold text-purple-400 mt-2">{averageAmount.toLocaleString()}</p>
          </div>
        </motion.div>

        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 mb-8 shadow-xl"
        >
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FiFilter size={20} /> Advanced Filters
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Agent Number Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Agent Number</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-3 text-purple-400" />
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white placeholder-gray-400"
                  placeholder="Enter agent ID"
                  value={filters.agent}
                  onChange={(e) => setFilters({ ...filters, agent: e.target.value })}
                />
              </div>
            </div>

            {/* Customer Number Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Customer Number</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-3 text-purple-400" />
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white placeholder-gray-400"
                  placeholder="Enter customer ID"
                  value={filters.customer}
                  onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
                />
              </div>
            </div>

            {/* Start Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Start Date</label>
              <input
                type="date"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>

            {/* End Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">End Date</label>
              <input
                type="date"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
          </div>

          {/* Sort and Filter Controls */}
          <div className="flex gap-4 items-end flex-wrap">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Sort By</label>
              <select
                className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {sortOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={loadBets}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-2 rounded-lg font-semibold transition shadow-lg"
            >
              Apply Filters
            </motion.button>
          </div>
        </motion.div>

        {/* Bets Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-xl overflow-hidden"
        >
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                <FiRefreshCw size={40} className="text-blue-400" />
              </motion.div>
            </div>
          ) : bets.length === 0 ? (
            <div className="p-16 text-center">
              <p className="text-gray-400 text-lg">No bets found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-purple-600 to-blue-600 border-b border-gray-700">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Reference</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Agent</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Customer</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Lucky No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Time</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-white">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bets.map((bet, idx) => (
                    <motion.tr
                      key={bet._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="border-b border-gray-700 hover:bg-gray-700 bg-opacity-50 transition group"
                    >
                      <td className="px-6 py-4 text-sm text-gray-300 font-mono group-hover:text-blue-400 transition">{bet._id.slice(-6)}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">{bet.agent_number}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">{bet.customer_number}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-green-400">{bet.amount}</td>
                      <td className="px-6 py-4 text-sm font-bold text-yellow-400">{bet.lucky_number}</td>
                      <td className="px-6 py-4 text-sm text-gray-400">{new Date(bet.timestamp).toLocaleString()}</td>
                      <td className="px-6 py-4 text-center">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white px-3 py-1 rounded-lg text-sm font-semibold transition inline-flex items-center gap-1"
                          onClick={() => setSelectedBet(bet)}
                        >
                          <FiEdit2 size={14} /> Edit
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        {/* Edit Modal */}
        {selectedBet && (
          <EditBetModal
            bet={selectedBet}
            onClose={() => {
              setSelectedBet(null);
              loadBets();
            }}
          />
        )}
      </motion.div>
    </div>
  );
};

export default LuckyDrawOversight;
